import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const NA = "Not applicable";

const isNA = (v) => v === NA || (Array.isArray(v) && v.length === 1 && v[0] === NA);

export const SynopticForm = ({ fields, values, onChange, disabled }) => {
  const set = (key, val) => onChange({ ...values, [key]: val });

  const toggleNA = (f) => {
    if (isNA(values[f.key])) {
      set(f.key, f.type === "multiselect" ? [] : "");
    } else {
      set(f.key, f.type === "multiselect" ? [NA] : NA);
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="synoptic-fields">
      {fields.map((f) => {
        const na = isNA(values[f.key]);
        const v = values[f.key];
        return (
          <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""} data-testid={`synoptic-field-${f.key}`}>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                {f.label}{f.required && <span className="text-red-700"> *</span>}
                {f.units ? <span className="ml-1 normal-case text-slate-400">({f.units})</span> : null}
              </Label>
              <button type="button" onClick={() => toggleNA(f)} disabled={disabled}
                data-testid={`synoptic-na-${f.key}`}
                className={`text-[10px] font-semibold uppercase tracking-wide ${na ? "text-[#134e4a]" : "text-slate-400 hover:text-slate-600"}`}>
                N/A
              </button>
            </div>

            {na ? (
              <div className="mt-1 flex h-11 items-center border border-dashed border-slate-300 bg-slate-50 px-3 text-sm text-slate-500">
                {NA}
              </div>
            ) : f.type === "select" ? (
              <select value={v || ""} onChange={(e) => set(f.key, e.target.value)} disabled={disabled}
                data-testid={`synoptic-input-${f.key}`} className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:bg-slate-100">
                <option value="">Select…</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === "multiselect" ? (
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`synoptic-input-${f.key}`}>
                {f.options.map((o) => {
                  const on = Array.isArray(v) && v.includes(o);
                  return (
                    <button key={o} type="button" disabled={disabled}
                      onClick={() => set(f.key, on ? v.filter((x) => x !== o) : [...(Array.isArray(v) ? v : []), o])}
                      className={`min-h-[36px] px-2 py-1 text-xs font-semibold ${on ? "bg-[#134e4a] text-white" : "border border-slate-300 text-slate-600"}`}>
                      {o}
                    </button>
                  );
                })}
              </div>
            ) : f.type === "boolean" ? (
              <div className="mt-1 flex gap-1.5" data-testid={`synoptic-input-${f.key}`}>
                {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                  <button key={lbl} type="button" disabled={disabled} onClick={() => set(f.key, val)}
                    className={`min-h-[44px] flex-1 px-3 text-sm font-semibold ${v === val ? "bg-[#134e4a] text-white" : "border border-slate-300 text-slate-700"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            ) : f.type === "textarea" ? (
              <Textarea value={v || ""} onChange={(e) => set(f.key, e.target.value)} disabled={disabled}
                data-testid={`synoptic-input-${f.key}`} className="mt-1 min-h-[80px] border-slate-300" />
            ) : (
              <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                value={v ?? ""} onChange={(e) => set(f.key, e.target.value)} disabled={disabled}
                data-testid={`synoptic-input-${f.key}`} className="mt-1 h-11 border-slate-300" />
            )}
            {f.help && <p className="mt-1 text-[11px] text-slate-500">{f.help}</p>}
          </div>
        );
      })}
    </div>
  );
};

export default SynopticForm;
